import { IToken, StardogSparqlParser } from 'millan';
import * as Monaco from 'monaco-editor/esm/vs/editor/editor.api';
import * as React from 'react';

const enum ParserRule {
  EmbeddedPropertyListPath = 'EmbeddedPropertyListPath',
  VarOrTerm = 'VarOrTerm',
  Verb = 'Verb',
}

export const useEditorIndentation = ({
  editorRef,
  indentationCtxRef,
}: {
  editorRef: React.MutableRefObject<Monaco.editor.IStandaloneCodeEditor | null>;
  indentationCtxRef: React.MutableRefObject<Monaco.editor.IContextKey<boolean> | null>;
}) => {
  const parserRef = React.useRef(new StardogSparqlParser());
  const indentationEditRef = React.useRef<{
    edits: Monaco.editor.IIdentifiedSingleEditOperation[];
    cursor: Monaco.Selection[];
  } | null>(null);
  const lastDocumentRef = React.useRef('');
  const lastTokensRef = React.useRef<IToken[]>([]);

  const doesTextSatisfyRule = React.useCallback(
    (text: string, entryRule: ParserRule) => {
      const parser = parserRef.current;
      parser.input = parser.tokenize(text);
      (parser as any)[entryRule]();
      return parser.errors.length === 0;
    },
    []
  );

  const setIndentationCtx = React.useCallback(
    ({ lineNumber, column }: Monaco.Position) => {
      if (!editorRef.current) {
        return;
      }

      const editor = editorRef.current;

      // tokenize the document if it has changed
      const document = editor.getValue();
      const didDocumentChange = document !== lastDocumentRef.current;
      if (didDocumentChange) {
        lastDocumentRef.current = document;
        lastTokensRef.current = parserRef.current.tokenize(document);
      }
      const tokens = lastTokensRef.current;

      // get the token before and at or after the cursor
      let tokenIdxBeforeCursor = -1;
      let tokenIdxAfterCursor = -1;
      for (let i = tokens.length - 1; i >= 0; i -= 1) {
        const token = tokens[i];
        if (
          token.startLine === lineNumber &&
          token.endLine === lineNumber &&
          token.startColumn !== undefined &&
          token.endColumn !== undefined &&
          token.startColumn < column &&
          token.endColumn < column
        ) {
          tokenIdxBeforeCursor = i;
          tokenIdxAfterCursor = i + 1;
          break;
        }
      }

      // exit if the token before the cursor is not a period or semicolon
      const tokenBeforeCursor = tokens[tokenIdxBeforeCursor];
      if (
        !tokenBeforeCursor ||
        !(tokenBeforeCursor.image === '.' || tokenBeforeCursor.image === ';')
      ) {
        indentationCtxRef.current?.reset();
        return;
      }

      // find the ealierst and second and third earliest tokens within the block
      let firstToken: IToken | null = null;
      let secondToken: IToken | null = null;
      let thirdToken: IToken | null = null;
      let collectionLevel = 0;
      let isBlankNodeList = false;
      let isMaybeAlternativeEdgeProperty = false;
      for (let i = tokenIdxBeforeCursor - 1; i >= 0; i -= 1) {
        const token = tokens[i];
        const { image } = token;

        if (image === '.') {
          // if we're at the end of a triples definition, exit
          break;
        } else if (image === '{' && !collectionLevel) {
          // We could be at the start of an alternative edge prop, but we could also
          // be at the start of a group graph pattern; this is confirmed outside the
          // loop
          isMaybeAlternativeEdgeProperty = true;
          break;
        } else if (image === '<<' && !collectionLevel) {
          // a left embed without a closing right embed first signifies an embedded triple
          break;
        } else if (image === '[' && !collectionLevel) {
          // a left brace without a closing right brace first signifies a blank node list
          isBlankNodeList = true;
          break;
        } else if (
          image === '>>' ||
          image === ']' ||
          image === ')' ||
          image === '}'
        ) {
          // if we're at the end of a collection or list or embedded triples,
          // increment the collection level
          collectionLevel += 1;
        } else if (
          image === '<<' ||
          image === '[' ||
          image === '(' ||
          image === '{'
        ) {
          // if we're at the start of a collection or list or embedded triples,
          // decrement the collection level
          collectionLevel -= 1;
          // if there are no more collections, reassign the first, second, and
          // third tokens, _except_ in the case of `{`, because the indentation
          // for that syntax should not actually align with the `{`
          if (!collectionLevel && image !== '{') {
            thirdToken = secondToken;
            secondToken = firstToken;
            firstToken = token;
          }
        } else if (!collectionLevel) {
          // ignore all the tokens within a collection and continue
          if (image === '!' || image === '^') {
            // reassign the most recent token to start at the beginning of the path
            firstToken = token;
          } else if (
            doesTextSatisfyRule(image, ParserRule.VarOrTerm) ||
            doesTextSatisfyRule(image, ParserRule.Verb) ||
            image === ';' ||
            image === '@prefix' // handle Turtle '@prefix' line special case
          ) {
            // reassign the first and second tokens
            thirdToken = secondToken;
            secondToken = firstToken;
            firstToken = token;
          }
        }
      }

      // exit if we didn't find any tokens
      if (!firstToken || !secondToken) {
        indentationCtxRef.current?.reset();
        return;
      }

      // If we may have an alternative edge property, try to disambiguate it
      // from a group graph pattern by matching the text to the relevant parser
      // rules
      let isAlternativeEdgeProperty = false;
      if (isMaybeAlternativeEdgeProperty) {
        const fullAlternativeEdgeProperty = `{ ${firstToken.image} ${
          secondToken.image
        }${thirdToken ? ` ${thirdToken.image}` : ''} }`;

        isAlternativeEdgeProperty = doesTextSatisfyRule(
          fullAlternativeEdgeProperty,
          ParserRule.EmbeddedPropertyListPath
        );
      }

      // align with the predicate of a blank node list (the first token)
      // align with the subject when at the end of a triple (the first token)
      // or align with the preidcate (the second token)
      const targetLine = lineNumber + 1;
      const targetColumn =
        isBlankNodeList ||
        isAlternativeEdgeProperty ||
        tokenBeforeCursor.image === '.' ||
        firstToken.image === '@prefix' // @prefix lines never get "continuation"-style indentation
          ? firstToken.startColumn || 0
          : secondToken.startColumn || 0;

      const insertRange = new Monaco.Range(
        lineNumber,
        column,
        lineNumber,
        column
      );
      const insertText = `\n${new Array(targetColumn).join(' ')}`;
      const edits = [{ range: insertRange, text: insertText }];

      const tokenAfterCursor = tokens[tokenIdxAfterCursor];
      if (tokenAfterCursor && tokenAfterCursor.startLine === lineNumber) {
        // trim whitespace before the next token if it's on the same line as the cursor
        const deleteRange = new Monaco.Range(
          lineNumber,
          column,
          lineNumber,
          tokenAfterCursor.startColumn || column
        );
        edits.unshift({ range: deleteRange, text: '' });
      }

      const selection = new Monaco.Selection(
        targetLine,
        targetColumn,
        targetLine,
        targetColumn
      );
      indentationEditRef.current = {
        edits,
        cursor: [selection, selection],
      };
      indentationCtxRef.current?.set(true);
    },
    [doesTextSatisfyRule, editorRef, indentationCtxRef]
  );

  const onDidChangeCursorPosition = React.useCallback(() => {
    if (!editorRef.current) {
      return;
    }
    const position = editorRef.current.getPosition();
    if (position) {
      setIndentationCtx(position);
    }
  }, [editorRef, setIndentationCtx]);

  const onEnter = React.useCallback(() => {
    if (!editorRef.current || !indentationEditRef.current) {
      return;
    }
    const { edits, cursor } = indentationEditRef.current;
    if (!edits || !cursor) {
      return;
    }

    editorRef.current.executeEdits('', edits, cursor);
    indentationCtxRef.current?.set(false);
  }, [editorRef, indentationCtxRef]);

  return { onDidChangeCursorPosition, onEnter };
};
